import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CbreAlertService } from '@cbre/alerts';
import { Column, filterHiddenColumns } from '@miq/catalog';
import { CommandSet } from '@shared/components/tree-data-table/commands/commands.model';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { COMPLEX_TABLE_COLUMNS_EXTEND } from './complex-table.const';
import {
  ChildRowReorderEvent,
  MiqSortEvent,
  PaginationSorting,
  RowChanges,
  RowReorderEvent,
  SingleChange,
  SortMode,
} from './complex-table.model';
import { ComplexTableStateService } from './complex-table.service';
import { SortEvent } from 'primeng/api';
import { TablePageEvent, TableRowReorderEvent } from 'primeng/table';
import { EMPTY_STRING } from '@miq/utils';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { BehaviorSubject } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'miq-complex-table',
  templateUrl: './complex-table.component.html',
  styleUrls: ['./complex-table.component.scss'],
  providers: [ComplexTableStateService],
  standalone: false,
})
export class ComplexTableComponent implements OnChanges, OnInit, OnDestroy {
  disableHeaderCheckbox = false;

  @Input() tableHeader: string;
  @Input() showRequiredIconTableHeader = false;
  @Input() title: string;
  @Input() childTitle: string;
  @Input() customSort = true;
  @Input() expandable = true;
  @Input() selectable = true;
  @Input() sortable = false;
  @Input() sortMode: SortMode = SortMode.Default;
  @Input() sortMessage = EMPTY_STRING;
  @Input() inMemoryPaginator = false;
  @Input() rows = 10;
  @Output() rowsChange = new EventEmitter<number>();
  @Input() rowsPerPageOptions = null;
  @Input() selectableChild = true;
  @Input() expandedMessage = 'recordsSearch.hideDetails';
  @Input() nonExpandedMessage = 'recordsSearch.showDetails';
  @Input() showCurrentPageReport = false;

  SortMode = SortMode;

  _sortOptions: PaginationSorting = {};

  @Input() set sortOptions(opts: PaginationSorting) {
    // we need to copy since we don't want to mutate the source
    // instance due to internal management has to be numeric
    // and source might be different, so table should be keeping
    // a copy of incoming state to not collide with client components
    this._sortOptions = { ...opts };
    if (typeof this._sortOptions.sortOrder === 'string') {
      this._sortOptions.sortOrder = this._sortOptions.sortOrder === 'asc' ? 1 : -1;
    }
  }

  @Input() elements: any[] = [];
  /**
   * used to support soft delete (if not provided, giving explicitly a fictitious attribute)
   * on parent level
   */
  @Input() hideElements = '__none__';
  /**
   * used to support soft delete (if not provided, giving explicitly a fictitious attribute)
   * on child level
   */
  @Input() hideChildElements = '__none__';

  @Input() parentColumns: any[] = [];
  @Input() parentCommands: CommandSet;

  @Input() childColumns: any[];
  @Input() childCommands: CommandSet;
  @Input() clickableRow = false;
  /**
   * if true, all columns received will pass through a
   * filter process to take out all columns with `hidden: true`
   */
  @Input() ignoreHiddenColumns = true;

  @Input() selectedResources: any[] = [];
  @Output() selectedResourcesChange = new EventEmitter<any[]>();

  @Input() selectedChildResources: any[] = [];
  @Output() selectedChildResourcesChange = new EventEmitter<any[]>();

  @Input() parentTableContextProvider: () => any;
  @Input() childTableContextProvider: () => any;

  @Output() parentChange = new EventEmitter<any>();
  @Output() parentRowChange = new EventEmitter<any>();
  @Output() childChange = new EventEmitter<SingleChange>();
  @Output() childRowChange = new EventEmitter<RowChanges>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() parentRowClick = new EventEmitter<any>();
  @Output() childRowClick = new EventEmitter<any>();
  @Output() sort = new EventEmitter<MiqSortEvent>();

  private sorting: { order?: number; field?: string } = {};

  @Input() parentIdentifierFn = self => self;
  @Input() childrenProvider = item => item.children;
  @Input() notify = message => this.cbreAlertService.showInfoSnackBar(message);
  @Input() showGoToRecordColumn = false;
  @Input() rowLinkGenerator: (x: Record<string, any>) => string[] = null;
  @Input() childShowGoToRecordColumn = false;
  @Input() childRowLinkGenerator: (x: Record<string, any>) => string[] = null;
  /**
   * Used to show the checkboxes but as disabled
   * @default false
   * @type {boolean}
   */
  @Input() disabledSelection = false;

  @Output() rowReorderEvent = new EventEmitter<RowReorderEvent>();
  @Output() onPage = new EventEmitter<TablePageEvent>();

  private _hasEDPWriteAccess = new BehaviorSubject<boolean>(false);
  public hasEDPWriteAccess$ = this._hasEDPWriteAccess.asObservable();

  iconColumnSize = 1;

  constructor(
    public tableStateService: ComplexTableStateService,
    private cbreAlertService: CbreAlertService,
    private userInfoResolver: UserInfoResolver
  ) {
    this.tableStateService.clear();
    this.tableStateService.lockSelection$
      .pipe(untilDestroyed(this))
      .subscribe(locked => (this.disableHeaderCheckbox = locked));
  }

  ngOnChanges({ parentColumns, childColumns, elements }: SimpleChanges) {
    if (parentColumns && parentColumns.currentValue && this.parentTableContextProvider) {
      this.parentColumns = this.doIgnoreHiddenColumns(this.parentColumns.map(this.parentTableContextProvider));
    }
    if (childColumns && childColumns.currentValue && this.childTableContextProvider) {
      this.childColumns = this.doIgnoreHiddenColumns(this.childColumns.map(this.childTableContextProvider));
    }
    if (this.expandable && this.elements && elements) {
      this.elements = this.elements.map(item => ({ ...item, children: this.childrenProvider(item) }));
      this.elements.forEach(
        element =>
          (element.expanded = this.tableStateService.isExpanded(this.parentIdentifierFn(element), 'company_id'))
      );
    }
  }

  ngOnInit() {
    /* required for static label translation */
    const isTrustedUser = this.userInfoResolver.isATrustedUser();
    this.userInfoResolver.hasEDPWriteAccess().then(hasEDPWriteAccess => {
      this._hasEDPWriteAccess.next(hasEDPWriteAccess && isTrustedUser);
    });

    this.parentColumns = this.doIgnoreHiddenColumns(this.parentColumns);
    if (this.childColumns) {
      this.childColumns = this.doIgnoreHiddenColumns(this.childColumns);
    }
    this.initColumnClickHandlers();

    // Determines the size of the icons column based on the number of features enabled
    this.iconColumnSize = [this.selectable, this.showGoToRecordColumn, this.sortMode === SortMode.Dragging].filter(
      Boolean
    ).length;
  }

  private initColumnClickHandlers() {
    (this.parentColumns || []).forEach(this.extendColumns);
    (this.childColumns || []).forEach(this.extendColumns);
  }

  private extendColumns(col: Column) {
    if (COMPLEX_TABLE_COLUMNS_EXTEND[col.columnId]) {
      Object.assign(col, { ...COMPLEX_TABLE_COLUMNS_EXTEND[col.columnId] });
    }
  }

  ngOnDestroy() {
    /* Required by untilDestroyed */
  }

  private doIgnoreHiddenColumns(columns: Column[]) {
    return this.ignoreHiddenColumns ? filterHiddenColumns(columns) : columns;
  }

  clearSelectionAndCheckSelectionBlock() {
    this.selectedResourcesChange.emit([]);
    this.tableStateService.unlockSelection();
  }

  toggle(row: any) {
    row.expanded = !row.expanded;
    if (row.expanded) {
      return this.tableStateService.setExpandedElement(this.parentIdentifierFn(row));
    }
    return this.tableStateService.setCollapsedElement(this.parentIdentifierFn(row));
  }

  onChange(change$) {
    if (!!change$.singleChange || (change$.target && change$.target.value)) {
      return this.parentChange.emit(change$);
    }
    this.selectedResourcesChange.emit([]);
    if (change$.changes && Object.values(change$.changes).length) {
      return this.parentRowChange.emit(new RowChanges(change$));
    }
  }

  onParentRowClick($event) {
    if (!!this.clickableRow) {
      this.parentRowClick.emit($event);
      this.rowClick.emit({ source: 'parent', rowInfo: $event });
    }
  }

  onChildRowClick($event) {
    if (!!this.clickableRow) {
      this.childRowClick.emit($event);
      this.rowClick.emit({ source: 'child', rowInfo: $event });
    }
  }

  onChildChange($childChange, parentData) {
    const { rowChange, singleChange } = $childChange;
    if (singleChange) {
      return this.childChange.emit({ ...$childChange, parentData });
    } else if (rowChange) {
      this.selectedChildResourcesChange.emit([]);
      return this.childRowChange.emit(new RowChanges({ ...$childChange, parentData }));
    }
  }

  handleInternalEvent(eventName: string, context: any[]) {
    return this.tableStateService.handleInternalEvent(this.elements, eventName, context);
  }

  emitSort($event: SortEvent) {
    if (this.sortable && this.sortChanged($event)) {
      this.sorting = $event;
      const column = this.parentColumns.find(col => col.field === $event.field);
      this.sort.emit({ ...this.sorting, column });
    }
  }

  private sortChanged(sortData: SortEvent) {
    return this.sorting.field !== sortData.field || this.sorting.order !== sortData.order;
  }

  /**
   * Factory RowReorderEvent and emit it
   * @param event
   * @param parentRow
   */
  public handleRowReorder(event: TableRowReorderEvent, parentRow?: Record<string, any>): void {
    this.rowReorderEvent.emit(
      parentRow == null ? new RowReorderEvent(event) : new ChildRowReorderEvent({ ...event, parentRow })
    );
  }
}
